import { createAction, handleActions } from 'redux-actions'
import client from 'common/util/http'
import { initializeFeatures } from 'common/util/features'
import { loadStart, loadFinish, loadError } from 'common/redux/root'
import {
  selectIsAuthenticated,
  selectFeaturesBootstrap,
  selectFeaturesClientSideId,
  selectFeaturesInitialized,
  selectFeaturesSecureModeHash,
  selectFeaturesUserProfile
} from 'common/redux/selectors/auth'
import { sanitizeRedirect } from 'common/util/redirect'

import moment from 'moment-timezone'

const LOAD = 'auth/LOAD'
const LOAD_FEATURES = 'auth/LOAD_FEATURES'
const RESET = 'auth/RESET'
export const LOGIN = 'auth/LOGIN'
export const LOGOUT = 'auth/LOGOUT'
export const LOAD_USER = 'auth/LOAD_USER'

export const load = createAction(LOAD)
const loadFeaturesAction = createAction(LOAD_FEATURES)
const reset = createAction(RESET)
const loginAction = createAction(LOGIN)
const logoutAction = createAction(LOGOUT)
export const loadUser = createAction(LOAD_USER)

const defaultErrorHandler = dispatch => error => {
  dispatch(loadError(error))
  return Promise.reject(error)
}

const resetHandler = dispatch => response => {
  dispatch(reset())
  dispatch(logoutAction())
  return Promise.resolve()
}

export const loadFeatures = () => async (dispatch, getState) => {
  const state = getState()

  const isAuthenticated = selectIsAuthenticated(state)
  if (!isAuthenticated) {
    return
  }

  const initialized = selectFeaturesInitialized(state)
  if (initialized) {
    return
  }

  const bootstrap = selectFeaturesBootstrap(state)
  const clientSideId = selectFeaturesClientSideId(state)
  const secureModeHash = selectFeaturesSecureModeHash(state)
  const userProfile = selectFeaturesUserProfile(state)

  initializeFeatures({bootstrap, clientSideId, secureModeHash, userProfile})

  dispatch(loadFeaturesAction({initialized: true}))
}

export const updateFeatures = (features) => async (dispatch) => {
  dispatch(loadFeaturesAction(features))
}

export function login(username, password, broker_op_id) {
  return dispatch => {
    dispatch(loadStart())

    client.post('login', {
      data: {
        username: username,
        password: password,
        broker_op_id: broker_op_id,
        tz: moment.tz.guess()
      }
    })
      .then(response => {
        dispatch(load(response))
        dispatch(loginAction(response))

        if (response.redirect_url) {
          window.location = response.redirect_url
        }
        if (response.user) {
          window.location.href = sanitizeRedirect()
        }

        dispatch(loadFinish())
      })
      .catch(error => dispatch(loadError(error)))
  }
}

export function logout(useLegacy) {
  const loginPath = useLegacy ? '/login_legacy' : '/login'
  return dispatch => {
    client.post('logout')
      .then(resetHandler)
      .then(() => window.location = loginPath)
  }
}

export function resetPassword(username) {
  return dispatch => {
    dispatch(loadStart())

    return client.post('reset_password', {
      data: {
        username: username
      }
    })
      .then(response => {
        dispatch(loadFinish())
        dispatch(load({...response, finished_reset: true}))
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function changePassword(reset_hash, password) {
  return dispatch => {
    dispatch(loadStart())

    return client.post('change_password', {
      data: {
        reset_hash,
        password
      }
    })
      .then(response => {
        dispatch(loadFinish())
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

export function changePasswordById(id, password) {
  return dispatch => {
    dispatch(loadStart())

    return client.post('change_password_by_user_id', {
      data: {
        user_id: id,
        password: password
      }
    })
      .then(response => {
        dispatch(loadFinish())
        return Promise.resolve(response)
      })
      .catch(dispatch(defaultErrorHandler))
  }
}

const initialState = {
  user: {
    features: {
      bootstrap: null,
      client_side_id: null,
      initiialized: false,
      secure_mode_hash: null,
      user_profile: null,
    }
  },
  enable_analytics: false,
  finished_reset: false
}

export default handleActions({
  [LOAD]: (state, {payload}) => ({ ...state, ...payload }),
  [LOAD_FEATURES]: (state, {payload}) => ({ ...state, user: {...state.user, features: {...state.user.features, ...payload }}}),
  [LOAD_USER]: (state, {payload}) => ({...state, user: {...state.user, ...payload}}),
  [RESET]: (state, {payload}) => ({...initialState})
}, initialState)
